import { type FC, forwardRef, useContext } from 'react';
import { CheckCircle } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMe } from '@/hooks/use-me';
import { formatDate } from '@/utils/format/date';
import { formatNumber } from '@/utils/format/number';
import { ConditionViolationLabel } from '@/warranty/domain/entities/condition-violation-status';
import { IWarrantyRepository } from '@/warranty/domain/repositories/warranty.repository';

import { WarrantyDetailsContext } from '../../pages/warranty-details';
import { GetConditionViolationStatus } from '../../queries/get-condition-violation-status';
import { TableSkeleton } from '../skeletons/TableSkeleton';

export const ZeroConditionTableStateRow: FC = () => {
  return (
    <tr className="border border-green-100 bg-green-50 text-green-800 shadow-inner">
      <td colSpan={6} className="p-6 text-center align-middle">
        <div className="flex flex-col items-center justify-center gap-2">
          <CheckCircle className="size-6 text-green-600" />
          <div className="text-lg font-semibold">
            No condition violation records detected
          </div>
          <div className="text-sm text-gray-600">
            All systems are operating within warranty conditions.
          </div>
        </div>
      </td>
    </tr>
  );
};

export type ConditionViolationsTableProps = {
  filters: IWarrantyRepository.Filters;
};
export const ConditionViolationsTable = forwardRef<
  HTMLDivElement,
  ConditionViolationsTableProps
>(function ({ filters }, ref) {
  const asset = useContext(WarrantyDetailsContext);
  const me = useMe();

  // get warranty result from dummy repository
  const conditionViolationStatus = GetConditionViolationStatus.useQuery({
    filters,
    assetFullFrontendId: asset!.fullFrontendId,
    assetId: asset!.id,
    customerId: me.config.id,
  });

  if (conditionViolationStatus.isLoading)
    return <TableSkeleton columns={6} rows={1} />;
  if (conditionViolationStatus.isError)
    return <div>Error loading Warranty overview</div>;

  // check if warrantyResult is undefined or empty
  if (
    !conditionViolationStatus.data ||
    conditionViolationStatus.data.length === 0
  ) {
    return (
      <div>
        <Table
          striped
          dense
          bleed
          ref={ref}
          className="size-full overflow-hidden"
        >
          <TableHead className="sticky top-0 bg-gray-100">
            <TableRow>
              <TableHeader className="text-center">Description</TableHeader>
              <TableHeader className="text-center">Extreme value</TableHeader>
              <TableHeader className="text-center">Latest Detect.</TableHeader>
              <TableHeader className="text-center">Duration</TableHeader>
              <TableHeader className="text-center">Total Count</TableHeader>
              <TableHeader className="text-center">SOH Penalty</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            <ZeroConditionTableStateRow />
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div>
      <Table
        striped
        dense
        bleed
        ref={ref}
        className="w-full table-auto overflow-hidden"
      >
        <TableHead className="sticky top-0 bg-gray-100">
          <TableRow>
            <TableHeader className="text-center">Description</TableHeader>
            <TableHeader narrow className="text-center">
              Extreme value
            </TableHeader>
            <TableHeader narrow className="text-center">
              Latest Detect.
            </TableHeader>
            <TableHeader narrow className="text-center">
              Duration
            </TableHeader>
            <TableHeader narrow className="text-center">
              Affected Units
            </TableHeader>
          </TableRow>
        </TableHead>

        <TableBody>
          {conditionViolationStatus.data.map((condViolation) => (
            <TableRow key={condViolation.id} className="hover:bg-gray-50">
              <TableCell wide justify="left">
                {ConditionViolationLabel[condViolation.description]}
              </TableCell>
              <TableCell narrow justify="center">
                {`${formatNumber(condViolation.extremeValue, 1)} ${condViolation.unit}`}
              </TableCell>
              <TableCell narrow justify="center">
                {formatDate(condViolation.latestDetect)}
              </TableCell>
              <TableCell narrow justify="center">
                {formatNumber(condViolation.duration, 1)} h
              </TableCell>
              <TableCell narrow justify="center">
                {condViolation.affectedUnits}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
});
ConditionViolationsTable.displayName = 'ConditionViolationsTable';
